/**
 * Excel export helpers and row mappers for medical PA / CMM-diff / external worklists.
 *
 * @module exportDataToExcel
 */
import moment from "moment";
import * as XLSX from "xlsx";
import { AuthStatusOptionModel } from "../data-model/authStatusOptions";
import { FinalWorklistDataResponse } from "../data-model/finalWorklistModel";
import {
  IndividualComment,
  MedicalPaOrder,
} from "../data-model/medicalPaOrdersModel";
import { formatDateToUTCWithFormat } from "./formatDate";
import { generateFullName } from "./generateOrderDataForTable";
import { getAuthStatusTextOfMedicalPaOrder } from "./getAuthStatusOfMedicalPaOrder";
import { getFinalStatusOfOrder } from "./getFinalStatusOfOrder";
import { capitalizeString } from "./stringModifications";

const fmt = (date: any, format: string) =>
  date && moment(date).isValid() ? moment(date).format(format) : "";

/**
 * Writes an array of row objects to an `.xlsx` file with preset column widths.
 *
 * @param data - JSON-serializable rows (e.g. from a table export mapper)
 * @param filename - Base name or full name; `.xlsx` is appended when missing
 */
export const exportDataToExcel = (data: any, filename: string) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths for better visibility
  const columnWidths = [
    { wch: 25 }, // order_id
    { wch: 25 }, // Patient Name
    { wch: 15 }, // Patient MRN
    { wch: 15 }, // Date of Service
    { wch: 40 }, // Medication
    { wch: 25 }, // Form Name
    { wch: 20 }, // CMM Key
    { wch: 15 }, // Form Diff
    { wch: 40 }, // Form Diff Details
    { wch: 15 }, // QA Diff
    { wch: 15 }, // Status
    { wch: 15 }, // Location
    { wch: 50 }, // Comment
    { wch: 15 }, // Record ID
  ];

  worksheet["!cols"] = columnWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const safeFilename = filename.endsWith(".xlsx")
    ? filename
    : `${filename}.xlsx`;
  XLSX.writeFile(workbook, safeFilename);
};

/**
 * Maps external-facing medical PA orders to flat key/value rows for spreadsheet export.
 *
 * @param data - Orders from the external medical PA worklist
 * @returns Array of plain objects suitable for `exportDataToExcel`; `[]` when input is empty
 */
export const exportDataFromTableForMedicalPaOrdersExternal = (
  data: MedicalPaOrder[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    const { authCommentsText, denialCommentsText } = getComments(item);
    return {
      "Date of Work": fmt(item?.timestamps?.date_of_work, "DD/MMM/YYYY"),
      "Patient Name": capitalizeString(
        generateFullName(
          item?.demographics?.first_name ?? "",
          "",
          item?.demographics?.last_name ?? "",
        ),
      ),
      "Patient DOB": item?.demographics?.date_of_birth ?? "",
      MRN:
        item?.demographics?.patient_id ?? item?.demographics?.patient_mrn ?? "",
      "Plan Name (Primary)": item?.coverage?.primary?.payer_name ?? "",
      "Member ID (Primary)": item?.coverage?.primary?.member_id ?? "",
      "Plan Name (Secondary)": item?.coverage?.secondary?.payer_name ?? "",
      "Member ID (Secondary)": item?.coverage?.secondary?.member_id ?? "",
      Medication: item?.payload?.regimen_name ?? "",
      "Date Of Service": formatDateToUTCWithFormat(
        item?.payload?.date_of_service ?? "",
        "MM/DD/YYYY",
        "",
      ),
      "Provider Name": capitalizeString(item?.payload?.practitioner_name ?? ""),
      "Auth Status": item?.auth_on_file?.auth_status ?? "",
      "BO Status": item?.status?.bo_status ?? "",
      Location: item?.payload?.location ?? "",
      "Auth Issue Comments": authCommentsText,
      "Denial Comments": denialCommentsText,
    };
  });
};

/**
 * Maps internal medical PA orders to flat key/value rows for spreadsheet export.
 *
 * @param data - Orders from the standard medical PA worklist
 * @returns Array of plain objects suitable for `exportDataToExcel`; `[]` when input is empty
 */
export const exportDataFromTableForMedicalPaOrders = (
  data: MedicalPaOrder[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    const {
      authCommentsText,
      authCommentsReason,
      denialCommentsText,
      denialCommentsReason,
    } = getComments(item);
    return {
      "Order ID": item?.id ?? "",
      "Created At": moment(item?.created_at).format("DD/MMM/YYYY") ?? "",
      "Date of Work": fmt(item?.timestamps?.date_of_work, "DD/MMM/YYYY"),
      "Patient Name": capitalizeString(
        generateFullName(
          item?.demographics?.first_name ?? "",
          "",
          item?.demographics?.last_name ?? "",
        ),
      ),
      "Patient DOB": item?.demographics?.date_of_birth ?? "",
      MRN:
        item?.demographics?.patient_id ?? item?.demographics?.patient_mrn ?? "",
      "Plan Name (Primary)": item?.coverage?.primary?.payer_name ?? "",
      "Member ID (Primary)": item?.coverage?.primary?.member_id ?? "",
      "Plan Name (Secondary)": item?.coverage?.secondary?.payer_name ?? "",
      "Member ID (Secondary)": item?.coverage?.secondary?.member_id ?? "",
      Medication: item?.payload?.regimen_name ?? "",
      "Date Of Service": formatDateToUTCWithFormat(
        item?.payload?.date_of_service ?? "",
        "MM/DD/YYYY",
        "",
      ),
      "Provider Name": capitalizeString(item?.payload?.practitioner_name ?? ""),
      "Auth Issue Comments": authCommentsText,
      "Auth Issue Reason": authCommentsReason,
      "BO Status": item?.status?.bo_status ?? "",
      "Auth Verification": item?.auth_on_file?.auth_status ?? "",
      Location: item?.payload?.location ?? "",
      Assignee: item?.assigned_to?.provider_name ?? "",
      "Denial Comments": denialCommentsText,
      "Denial Reason": denialCommentsReason,
    };
  });
};

/**
 * Maps pharma PA CMM diff table rows to flat objects for Excel export.
 *
 * @param tableData - Rows from the CMM diff comparison table
 * @returns Array of label/value pairs; `[]` when input is empty
 */
export const exportDataFromTableForCmmDiffData = (tableData: any[]) => {
  if (tableData.length === 0) {
    return [];
  }
  return tableData.map((item) => {
    return {
      "Patient Name": item.pharmaPaDiffPatientDetails?.mainText ?? "--",
      "Patient MRN":
        item.pharmaPaDiffPatientDetails?.secondaryText?.replace("MRN : ", "") ??
        "--",
      "Date of Service": item.pharmaPaDiffDateOfService ?? "--",
      Medication: item.pharmaPaDiffMedication ?? "--",
      "Form Name": item.pharmaPaDiffFormName ?? "--",
      "CMM Key": item.pharmaPaDiffCmmKey ?? "--",
      "Form Diff": item.pharmaPaDiffFormDiffFields?.text ?? "--",
      "Form Diff Details": item.pharmaPaDiffFormDiffFields?.displayText ?? "--",
      "QA Diff": item.pharmaPaDiffQaDiffFields?.text ?? "--",
      Status: item.pharmaPaDiffStatus?.text ?? "--",
      Comment: item.pharmaPaDiffCommentString ?? "--",
      "Record ID": item.id ?? "--",
    };
  });
};

/**
 * Maps final worklist API rows to flat objects for external PA spreadsheet export.
 *
 * @param data - Final worklist order rows
 * @param authStatusOptions - Unused in this mapper; retained for a stable exporter signature
 * @returns Row objects for Excel; `[]` when input is empty
 */
export const exportDataFromTableForExternalPaOrders = (
  data: FinalWorklistDataResponse[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    const { authCommentsText, denialCommentsText } = getComments(item);
    return {
      "Patient Name": capitalizeString(
        generateFullName(item?.patient_name ?? "", "", ""),
      ),
      "Patient MRN": item?.mrn ?? "",
      "Date of Service": formatDateToUTCWithFormat(
        item?.date_of_service ?? "",
        "MM/DD/YYYY",
        "",
      ),
      "Date of Work (DoW)": fmt(item?.date_of_work, "MM/DD/YYYY"),
      "Plan Name (Primary)": item?.primary_insurance ?? "",
      "Plan Name (Secondary)": item?.secondary_ins ?? "",
      "Date of Birth (DoB)": moment(item?.dob).format("MM/DD/YYYY") ?? "",
      "ID (Insurance ID)": item?.id ?? "",
      Medication: item?.regimen ?? "",
      Location: item?.location ?? "",
      "Provider Name": capitalizeString(item?.md ?? ""),
      "Created At": moment(item?.date_of_work).format("MM/DD/YYYY") ?? "",
      "Auth Verification": item?.auth_status ?? "",
      "BO status": getFinalStatusOfOrder(item?.bo_value).text ?? "",
      "Auth Issue Comments": authCommentsText,
      "Denial Comments": denialCommentsText,
    };
  });
};

/**
 * Maps {@link MedicalPaOrder} records to external-style export rows with resolved auth status text.
 *
 * @param data - Medical PA orders
 * @param authStatusOptions - Options used by {@link getAuthStatusTextOfMedicalPaOrder}
 * @returns Row objects for Excel; `[]` when input is empty
 */
export const exportDataFromTableForExternalPaOrdersNew = (
  data: MedicalPaOrder[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  if (data.length === 0) {
    return [];
  }
  return data.map((item) => {
    const { authCommentsText, denialCommentsText } = getComments(item);
    return {
      "Date Of Work": fmt(item?.timestamps?.date_of_work, "MM/DD/YYYY"),
      "Patient MRN":
        item?.demographics?.patient_id ?? item?.demographics?.patient_mrn ?? "",
      "Patient Name": capitalizeString(
        generateFullName(
          item?.demographics?.first_name ?? "",
          "",
          item?.demographics?.last_name ?? "",
        ),
      ),
      "Date Of Birth": item?.demographics?.date_of_birth ?? "",
      MD: item?.payload?.practitioner_name ?? "",
      "Plan Name (Primary)": item?.coverage?.primary?.payer_name ?? "",
      "Member ID (Primary)": item?.coverage?.primary?.member_id ?? "",
      "Plan Name (Secondary)": item?.coverage?.secondary?.payer_name ?? "",
      "Member ID (Secondary)": item?.coverage?.secondary?.member_id ?? "",
      Medication: item?.payload?.regimen_name ?? "",
      "Date Of Service": formatDateToUTCWithFormat(
        item?.payload?.date_of_service ?? "",
        "lll",
        "",
      ),
      "Auth Status":
        getAuthStatusTextOfMedicalPaOrder(item, authStatusOptions ?? []) ?? "",
      "BO Status": item?.status?.bo_status ?? "",
      Location: item?.payload?.location ?? "",
      "Auth Issue Comments": authCommentsText,
      "Denial Comments": denialCommentsText,
    };
  });
};

/**
 * Joins query and denial comment threads into four string fields for export columns.
 *
 * @param item - Order whose `comments.query_comments` / `denial_comments` are read
 * @returns Concatenated comment and reason strings (empty strings when none)
 */
export const getComments = (item: MedicalPaOrder) => {
  const authComments = item?.comments?.query_comments ?? [];
  const authCommentsText = authComments
    ?.map((item: IndividualComment) => item.comment)
    .join(", ");
  const authCommentsReason = authComments
    ?.map((item: IndividualComment) => item.reason)
    .join(", ");
  const denialComments = item?.comments?.denial_comments ?? [];
  const denialCommentsText = denialComments
    ?.map((item: IndividualComment) => item.comment)
    .join(", ");
  const denialCommentsReason = denialComments
    ?.map((item: IndividualComment) => item.reason)
    .join(", ");
  return {
    authCommentsText: authCommentsText ?? "",
    authCommentsReason: authCommentsReason ?? "",
    denialCommentsText: denialCommentsText ?? "",
    denialCommentsReason: denialCommentsReason ?? "",
  };
};
